import { array } from 'fp-ts';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getCountryOptions } from '@mews-commander/legacy-entities/src/ducks/countries';
import { getCountrySubdivisionOptions } from '@mews-commander/legacy-entities/src/ducks/countrySubdivisions';
import { Option } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { UnicodeEntity } from '@mews/enums';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DropdownItem, Size } from '@optimus-web/core';

interface CountrySelectFieldProps {
    countryFieldName: string;
    countrySubdivisionFieldName?: string;
    disabled?: Partial<Record<string, boolean>>;
    required?: Partial<Record<string, boolean>>;
    size?: Size;
}

const renderOptionListItems = ({ value, label }: Option) => (
    <DropdownItem key={value} value={value as string} label={label} />
);

const emptyOption = <DropdownItem value="" label={UnicodeEntity.Dash} />;

export const CountrySelectField = ({
    countryFieldName,
    countrySubdivisionFieldName,
    required,
    size,
    disabled,
}: CountrySelectFieldProps) => {
    const { setValue, getFieldState } = useFormContext();

    const countryCodeFieldState = getFieldState(countryFieldName);
    const countryCode = useWatch({ name: countryFieldName });

    const countryOptions = useCommanderSelector(getCountryOptions);
    const countrySubdivisionOptions = useCommanderSelector((state) =>
        countryCode ? getCountrySubdivisionOptions(state, { countryCode }) : []
    );

    /**
     * Avoid retaining the previously selected country subdivision on country change.
     *
     * Fixes this scenario:
     * - Select a country with a country subdivision.
     * - Select a country subdivision.
     * - Change the selected to a country without a subdivision.
     * - Submit the form.
     *
     * Without the following code, the form would be submitted with
     * the previously selected country subdivision, even though the
     * currently selected country does not have subdivisions.
     */
    useEffect(() => {
        if (countryCodeFieldState.isDirty && countrySubdivisionFieldName) {
            setValue(countrySubdivisionFieldName, '', { shouldDirty: true });
        }
    }, [countryCode, countryCodeFieldState.isDirty, countrySubdivisionFieldName, setValue]);

    return (
        <>
            <FormSelectField
                name={countryFieldName}
                data-test-select-name={countryFieldName}
                label={<LocalizedText textKey={TextKey.Country} />}
                required={required?.[countryFieldName]}
                disabled={disabled?.[countryFieldName]}
                size={size}
            >
                {emptyOption}
                {countryOptions.map(renderOptionListItems)}
            </FormSelectField>
            {countrySubdivisionFieldName && array.isNonEmpty(countrySubdivisionOptions) ? (
                <FormSelectField
                    name={countrySubdivisionFieldName}
                    data-test-select-name={countrySubdivisionFieldName}
                    label={<LocalizedText textKey={TextKey.CountrySubdivision} />}
                    required={required?.[countrySubdivisionFieldName]}
                    size={size}
                    disabled={disabled?.[countrySubdivisionFieldName]}
                >
                    {emptyOption}
                    {countrySubdivisionOptions.map(renderOptionListItems)}
                </FormSelectField>
            ) : null}
        </>
    );
};
